import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarPlus, CircleNotch, Sparkle, Warning } from '@phosphor-icons/react';

import { listTeams } from '@/api/teams';
import { getSchedulingRulesSummary } from '@/api/teamCategories';
import {
  addSlot,
  applyTemplate,
  createTemplate,
  enqueueTemplateDraft,
  getTemplateDraftJob,
  previewApplyTemplate,
  type DraftSlot,
  type TemplateDraft,
} from '@/api/templates';
import { GETTING_STARTED_QUERY_KEY } from '@/components/GettingStartedCard';
import { useConfirm } from '@/components/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { WizardDialog } from '@/components/setup/WizardDialog';

type Phase = 'intro' | 'generating' | 'review' | 'saved';

// ISO Monday (YYYY-MM-DD) of the week `addWeeks` from `base`. Apply spans the
// current week through +7 (8 weeks) so a fresh calendar fills out immediately.
function isoMonday(base: Date, addWeeks = 0): string {
  const d = new Date(base);
  const dow = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - dow + addWeeks * 7);
  return d.toISOString().slice(0, 10);
}
const APPLY_WEEKS = 8;

// The AI draft job runs server-side, so closing the dialog must not throw the
// run away. We persist the in-flight job id (and the saved template id, once
// created) per org in sessionStorage; on reopen the generator rehydrates and
// reattaches to the still-running or already-finished job instead of starting
// over. Cleared when the flow completes (applied) or the job fails.
type PersistedSchedule = { jobId: string | null; templateId: string | null };

function scheduleGenKey(slug: string): string {
  return `sa-schedule-gen:${slug}`;
}
function readScheduleGen(slug: string): PersistedSchedule | null {
  try {
    const raw = sessionStorage.getItem(scheduleGenKey(slug));
    return raw ? (JSON.parse(raw) as PersistedSchedule) : null;
  } catch {
    return null;
  }
}
function writeScheduleGen(slug: string, v: PersistedSchedule): void {
  try {
    sessionStorage.setItem(scheduleGenKey(slug), JSON.stringify(v));
  } catch {
    /* storage unavailable — degrade to non-persistent */
  }
}
function clearScheduleGen(slug: string): void {
  try {
    sessionStorage.removeItem(scheduleGenKey(slug));
  } catch {
    /* ignore */
  }
}

// ScheduleGenerator: generate a weekly training schedule with AI (async draft
// job), review it, save it as a template directly (human-initiated — no
// CommandIntent), then optionally apply it to the calendar behind an explicit
// confirm (wide blast radius). Lights up create_template on save and
// apply_schedule on apply → wizard confetti.
export function ScheduleGenerator({
  slug,
  open,
  onOpenChange,
}: {
  slug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  // Rehydrate any in-flight/finished run for this org so a close+reopen resumes
  // exactly where it left off. templateId present → already saved (offer apply);
  // else jobId present → reattach to the draft job (re-poll lands it in review).
  const [persisted] = useState<PersistedSchedule | null>(() => readScheduleGen(slug));
  const [phase, setPhase] = useState<Phase>(() =>
    persisted?.templateId ? 'saved' : persisted?.jobId ? 'generating' : 'intro',
  );
  const [jobId, setJobId] = useState<string | null>(() => persisted?.jobId ?? null);
  const [draft, setDraft] = useState<TemplateDraft | null>(null);
  const [templateId, setTemplateId] = useState<string | null>(() => persisted?.templateId ?? null);
  const [error, setError] = useState<string | null>(null);

  const templateName = t('setupWizard.schedule.templateName');

  const teamsQuery = useQuery({
    queryKey: ['teams', slug, 'wizard-schedule'],
    queryFn: () => listTeams(token, slug, { limit: 200 }),
    enabled: !!token && open,
  });
  const teamIds = useMemo(
    () => (teamsQuery.data?.teams ?? []).map((tm) => tm.id),
    [teamsQuery.data],
  );

  // The AI grounds the schedule on each team's scheduling rules, so a team with
  // no rules can't be drafted (the backend would skip it, and if NONE have
  // rules the job fails with a dead-end error). Preflight with the rules
  // summary: draft only the teams that have rules, and if none do, guide the
  // user to set rules up first instead of letting them hit a failed run.
  const rulesSummaryQuery = useQuery({
    queryKey: ['scheduling-rules-summary', slug, 'wizard-schedule'],
    queryFn: () => getSchedulingRulesSummary(token, slug),
    enabled: !!token && open,
  });
  const eligibleTeamIds = useMemo(() => {
    const withRules = new Set(
      (rulesSummaryQuery.data?.teams ?? []).filter((r) => r.has_rules).map((r) => r.team_id),
    );
    return teamIds.filter((id) => withRules.has(id));
  }, [teamIds, rulesSummaryQuery.data]);

  // Poll the async draft job while generating.
  const jobQuery = useQuery({
    queryKey: ['template-draft-job', slug, jobId],
    queryFn: () => getTemplateDraftJob(token, slug, jobId as string),
    enabled: !!token && !!jobId && phase === 'generating',
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === 'pending' || s === 'running' ? 1500 : false;
    },
  });

  // React to terminal job states (in an effect — never setState during render).
  const job = jobQuery.data;
  useEffect(() => {
    if (phase !== 'generating' || !job) return;
    if (job.status === 'completed' && job.draft) {
      setDraft(job.draft);
      setPhase('review');
    } else if (job.status === 'failed' || job.status === 'cancelled') {
      clearScheduleGen(slug);
      setJobId(null);
      setError(t('setupWizard.schedule.generateError'));
      setPhase('intro');
    }
    // t is stable enough; we only care about job/phase transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, phase]);

  // A rehydrated job id may no longer exist (e.g. after a DB reset) — the poll
  // then errors. Drop the stale reference and start clean rather than spin.
  useEffect(() => {
    if (phase === 'generating' && jobId && jobQuery.isError) {
      clearScheduleGen(slug);
      setJobId(null);
      setError(t('setupWizard.schedule.generateError'));
      setPhase('intro');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, jobId, jobQuery.isError]);

  const enqueueMutation = useMutation({
    mutationFn: () =>
      enqueueTemplateDraft(token, slug, {
        team_ids: eligibleTeamIds,
        mode: 'new',
        template_name: templateName,
        locale: i18n.language,
      }),
    onSuccess: (res) => {
      setJobId(res.job_id);
      setPhase('generating');
      writeScheduleGen(slug, { jobId: res.job_id, templateId: null });
    },
    onError: () => setError(t('setupWizard.schedule.generateError')),
  });

  const saveMutation = useMutation({
    mutationFn: async (slots: DraftSlot[]) => {
      const tpl = await createTemplate(token, slug, { name: templateName });
      for (const s of slots) {
        await addSlot(token, slug, tpl.id, {
          day_of_week: s.day_of_week,
          start_time: s.start_time,
          end_time: s.end_time,
          title: s.title,
          kind: s.kind,
          ...(s.description ? { description: s.description } : {}),
          location_id: s.location_id ?? null,
          zone_id: s.zone_id ?? null,
          team_id: s.team_id,
        });
      }
      return tpl.id;
    },
    onSuccess: (id) => {
      setTemplateId(id);
      setPhase('saved');
      writeScheduleGen(slug, { jobId, templateId: id });
      queryClient.invalidateQueries({ queryKey: GETTING_STARTED_QUERY_KEY(slug) });
      queryClient.invalidateQueries({ queryKey: ['templates', slug] });
    },
    onError: () => setError(t('setupWizard.schedule.saveError')),
  });

  const applyMutation = useMutation({
    mutationFn: async () => {
      if (!templateId) return;
      const from = isoMonday(new Date(), 0);
      const to = isoMonday(new Date(), APPLY_WEEKS - 1);
      const preview = await previewApplyTemplate(token, slug, templateId, from, to);
      const plannedCount = preview.planned.length;
      const conflictCount = preview.conflicts.length;
      const ok = await confirm({
        title: t('setupWizard.schedule.applyConfirmTitle'),
        body: t('setupWizard.schedule.applyConfirmBody', {
          count: plannedCount,
          weeks: APPLY_WEEKS,
          conflicts: conflictCount,
        }),
        confirmLabel: t('setupWizard.schedule.applyConfirmCta'),
      });
      if (!ok) return;
      await applyTemplate(token, slug, templateId, from, to);
      return true;
    },
    onSuccess: (applied) => {
      if (!applied) return;
      clearScheduleGen(slug); // flow complete — nothing left to resume
      queryClient.invalidateQueries({ queryKey: GETTING_STARTED_QUERY_KEY(slug) });
      queryClient.invalidateQueries({ queryKey: ['activities', slug] });
      onOpenChange(false);
    },
    onError: () => setError(t('setupWizard.schedule.applyError')),
  });

  const handleOpenChange = (next: boolean) => {
    // Closing no longer resets: the run is persisted (sessionStorage) and the
    // draft job keeps going server-side, so reopening resumes where it was.
    // The in-flight/saved run is cleared only when the flow completes (apply)
    // or the job fails — see the mutations/effects above.
    onOpenChange(next);
  };

  return (
    <WizardDialog
      open={open}
      onOpenChange={handleOpenChange}
      icon={CalendarPlus}
      title={t('setupWizard.schedule.title')}
      subtitle={t('setupWizard.schedule.subtitle')}
    >
      {renderBody()}
    </WizardDialog>
  );

  function renderBody() {
    if (teamsQuery.isLoading || rulesSummaryQuery.isLoading) {
      return <BodyLoading label={t('common.loading')} />;
    }
    if (teamIds.length === 0) {
      return (
        <div className="flex flex-col items-start gap-4 py-2">
          <p className="text-sm text-muted-foreground">{t('setupWizard.schedule.noTeams')}</p>
          <Button
            onClick={() => {
              handleOpenChange(false);
              navigate(`/orgs/${slug}/teams`);
            }}
          >
            {t('setupWizard.schedule.noTeamsCta')}
          </Button>
        </div>
      );
    }
    if (phase === 'generating') {
      return (
        <BodyLoading
          label={t('setupWizard.schedule.generating', { count: eligibleTeamIds.length })}
          {...(job?.progress ? { progress: job.progress } : {})}
        />
      );
    }
    if (phase === 'review' && draft) {
      return renderReview(draft);
    }
    if (phase === 'saved') {
      return renderSaved();
    }
    // No team has scheduling rules yet → the AI has nothing to ground on.
    // Send the user to set rules up (team categories) rather than fail a run.
    if (eligibleTeamIds.length === 0) {
      return (
        <div className="flex flex-col items-start gap-4 py-2">
          <p className="text-sm text-muted-foreground">{t('setupWizard.schedule.noRules')}</p>
          <Button
            onClick={() => {
              handleOpenChange(false);
              navigate(`/orgs/${slug}/settings/team-categories`);
            }}
          >
            {t('setupWizard.schedule.noRulesCta')}
          </Button>
        </div>
      );
    }
    // intro
    return (
      <div className="flex flex-col items-start gap-4 py-2">
        <p className="text-sm text-muted-foreground">
          {t('setupWizard.schedule.intro', { count: eligibleTeamIds.length })}
        </p>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button
          onClick={() => {
            setError(null);
            enqueueMutation.mutate();
          }}
          disabled={enqueueMutation.isPending}
        >
          <Sparkle size={16} weight="fill" />
          {t('setupWizard.schedule.generateCta')}
        </Button>
      </div>
    );
  }

  function renderReview(d: TemplateDraft) {
    const included = d.summary.per_team.filter((p) => p.included);
    const warnings = d.summary.warnings;
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          {t('setupWizard.schedule.reviewIntro', { slots: d.slots.length, teams: included.length })}
        </p>
        <ul className="flex flex-col divide-y divide-border rounded-xl border border-border">
          {included.map((p) => (
            <li key={p.team_id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
              <span className="min-w-0 truncate font-medium text-foreground">{p.team_name}</span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {t('setupWizard.schedule.perTeam', {
                  slots: p.slot_count,
                  minutes: p.total_minutes,
                })}
              </span>
            </li>
          ))}
        </ul>
        {warnings.length > 0 && (
          <div className="flex items-start gap-2 rounded-xl bg-muted/60 px-4 py-3 text-xs text-muted-foreground">
            <Warning size={16} className="mt-0.5 shrink-0 text-foreground/60" />
            <span>{t('setupWizard.schedule.warnings', { count: warnings.length })}</span>
          </div>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={saveMutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              saveMutation.mutate(d.slots);
            }}
            disabled={saveMutation.isPending || d.slots.length === 0}
          >
            {saveMutation.isPending
              ? t('setupWizard.schedule.saving')
              : t('setupWizard.schedule.saveCta')}
          </Button>
        </div>
      </div>
    );
  }

  function renderSaved() {
    return (
      <div className="flex flex-col items-start gap-4 py-2">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Sparkle size={18} weight="fill" className="text-primary" />
          {t('setupWizard.schedule.savedTitle')}
        </div>
        <p className="text-sm text-muted-foreground">{t('setupWizard.schedule.savedBody')}</p>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t('setupWizard.schedule.savedNotNow')}
          </Button>
          <Button onClick={() => applyMutation.mutate()} disabled={applyMutation.isPending}>
            <CalendarPlus size={16} weight="bold" />
            {applyMutation.isPending
              ? t('setupWizard.schedule.applying')
              : t('setupWizard.schedule.applyCta')}
          </Button>
        </div>
      </div>
    );
  }
}

function BodyLoading({
  label,
  progress,
}: {
  label: string;
  progress?: { batches_total: number; batches_done: number; slots_emitted: number };
}) {
  const pct =
    progress && progress.batches_total > 0
      ? Math.round((progress.batches_done / progress.batches_total) * 100)
      : null;
  return (
    <div className="flex flex-col items-center gap-4 py-8 text-center">
      <CircleNotch size={32} className="animate-spin text-primary motion-reduce:animate-none" />
      <p className="text-sm text-muted-foreground">{label}</p>
      {pct !== null && (
        <div className="h-2 w-48 overflow-hidden rounded-pill bg-muted">
          <div
            className="h-full rounded-pill bg-primary transition-[width] duration-500 motion-reduce:transition-none"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}
